import React, { useEffect, useState } from 'react';
import MenuBookIcon from '@/assets/icons/menu-book-white.svg?react';
import ShuttleIcon from '@/assets/icons/airport-shuttle-white.svg?react';
import RestaurantIcon from '@/assets/icons/restaurant-white.svg?react';
import { Modal, generateFingerprint } from '@sokcho-girls/common';
import './ReservationModal.scss';

interface ReservationModalProps {
    isOpen: boolean;
    onClose: () => void;
}

interface StudySeatReservation {
    reservationId: number;
    seatNumber: string;
    startTime: string;
    endTime: string;
    status: 'RESERVED' | 'CANCELLED' | 'CHECKED_IN' | 'COMPLETED' | 'NO_SHOW';
}

interface BusReservation {
    reservationId: number;
    busName: string;
    plateNumber: string;
    departureTime: string;
    status: 'RESERVED' | 'CANCELLED' | 'CHECKED_IN' | 'COMPLETED' | 'NO_SHOW';
}

interface DinnerReservation {
    reservationId: number;
    dinnerTime: string;
    status: 'RESERVED' | 'CANCELLED' | 'CHECKED_IN' | 'COMPLETED' | 'NO_SHOW';
}

interface MyReservationsResponse {
    studySeatReservations: StudySeatReservation[];
    busReservations: BusReservation[];
    dinnerReservations: DinnerReservation[];
}

export const ReservationModal: React.FC<ReservationModalProps> = ({ isOpen, onClose }) => {
    const [reservations, setReservations] = useState<MyReservationsResponse>({ studySeatReservations: [], busReservations: [], dinnerReservations: [] });
    const [isLoading, setIsLoading] = useState(false);
    const [isEntryAllowed, setIsEntryAllowed] = useState(false);
    const [fingerprint, setFingerprint] = useState<string>('');

    const fetchReservations = async () => {
        setIsLoading(true);
        try {
            const response = await fetch('/api/center/users/me/reservations');
            if (response.ok) {
                const data = await response.json();
                setReservations(data);
            } else {
                console.error('Failed to fetch reservations');
            }
        } catch (error) {
            console.error('Error fetching reservations:', error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (isOpen) {
            fetchReservations();

            // 디바이스 입장 허용 여부 확인
            const checkEntryAllowed = async () => {
                try {
                    const fp = await generateFingerprint();
                    setFingerprint(fp);
                    const response = await fetch(`/api/center/devices/entry-allowed?fingerprint=${encodeURIComponent(fp)}`);
                    if (response.ok) {
                        const data = await response.json();
                        setIsEntryAllowed(data.studyRoomEntryAllowed);
                    }
                } catch (error) {
                    console.error('Failed to check entry allowed:', error);
                    setIsEntryAllowed(false);
                }
            };
            checkEntryAllowed();
        }
    }, [isOpen]);

    const handleCheckIn = async (reservationId: number) => {
        if (!confirm('입장하시겠습니까?')) return;
        try {
            const response = await fetch(`/api/center/study-seats/reservations/${reservationId}/check-in`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ fingerprint })
            });
            if (response.ok) {
                alert('입장 처리되었습니다.');
                fetchReservations();
            } else {
                const error = await response.json().catch(() => ({}));
                alert(error.message || '입장에 실패했습니다.');
            }
        } catch (error) {
            console.error('Check-in failed:', error);
            alert('오류가 발생했습니다.');
        }
    };

    const handleCancelReservation = async (reservationId: number, type: 'study-seat' | 'bus' | 'dinner') => {
        if (!confirm('예약을 취소하시겠습니까?')) return;

        const endpoint = type === 'study-seat'
            ? `/api/center/study-seats/reservations/${reservationId}`
            : type === 'bus'
                ? `/api/center/buses/reservations/${reservationId}`
                : `/api/center/dinners/reservations/${reservationId}`;

        try {
            const response = await fetch(endpoint, {
                method: 'DELETE'
            });
            if (response.ok) {
                alert('취소되었습니다.');
                fetchReservations();
            } else {
                alert('취소에 실패했습니다.');
            }
        } catch (error) {
            console.error('Cancel failed:', error);
            alert('오류가 발생했습니다.');
        }
    };

    const formatTime = (timeStr: string) => timeStr.substring(0, 5);

    const isEntryTime = (startTimeStr: string) => {
        const now = new Date();
        const [hours, minutes] = startTimeStr.split(':').map(Number);

        const startTime = new Date();
        startTime.setHours(hours, minutes, 0, 0);

        const tenMinutesBefore = new Date(startTime.getTime() - 10 * 60 * 1000);
        const tenMinutesAfter = new Date(startTime.getTime() + 10 * 60 * 1000);

        return now >= tenMinutesBefore && now <= tenMinutesAfter;
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title="예약 내역"
            size="lg"
            overlayClassName="reservation-modal-overlay"
            modalClassName="reservation-modal-shell"
            contentClassName="reservation-modal-shell__content"
        >
            <div className="reservation-modal">
                {isLoading ? (
                    <div className="loading">불러오는 중...</div>
                ) : (
                    <>
                        <section className="reservation-section">
                            <h3 className="reservation-section__title">
                                <MenuBookIcon className="icon icon-book" /> 스터디카페
                            </h3>
                            <div className="reservation-list">
                                {reservations.studySeatReservations.length === 0 ? (
                                    <div className="reservation-empty">예약된 내역이 없습니다.</div>
                                ) : (
                                    reservations.studySeatReservations.map((res) => {
                                        const showEntryBtn = res.status === 'RESERVED' && isEntryTime(res.startTime) && isEntryAllowed;
                                        const showCancelBtn = res.status === 'RESERVED';

                                        let statusText = '';
                                        let statusClass = '';

                                        switch (res.status) {
                                            case 'RESERVED': statusText = '예약됨'; statusClass = 'pending'; break;
                                            case 'CHECKED_IN': statusText = '사용중'; statusClass = 'using'; break;
                                            case 'COMPLETED': statusText = '사용완료'; statusClass = 'completed'; break;
                                            case 'NO_SHOW': statusText = '미방문'; statusClass = 'no-show'; break;
                                            case 'CANCELLED': statusText = '취소됨'; statusClass = 'cancelled'; break;
                                        }

                                        return (
                                            <div key={res.reservationId} className="reservation-item">
                                                <span className="reservation-item__info">
                                                    {formatTime(res.startTime)} ~ {formatTime(res.endTime)} ({res.seatNumber}번 좌석)
                                                </span>
                                                <div className="reservation-item__actions">
                                                    {!showEntryBtn &&
                                                        <span className={`status status--${statusClass}`}>
                                                            {statusText}
                                                        </span>
                                                    }
                                                    {showEntryBtn && (
                                                        <button
                                                            className="btn-entry"
                                                            onClick={() => handleCheckIn(res.reservationId)}
                                                        >
                                                            입장
                                                        </button>
                                                    )}
                                                    {showCancelBtn && (
                                                        <button
                                                            className="btn-cancel"
                                                            onClick={() => handleCancelReservation(res.reservationId, 'study-seat')}
                                                        >
                                                            취소
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })
                                )}
                            </div>
                        </section>

                        <section className="reservation-section">
                            <h3 className="reservation-section__title">
                                <ShuttleIcon className="icon icon-shuttle" /> 별빛버스
                            </h3>
                            <div className="reservation-list">
                                {reservations.busReservations.length === 0 ? (
                                    <div className="reservation-empty">예약된 내역이 없습니다.</div>
                                ) : (
                                    reservations.busReservations.map((res) => {
                                        const showCancelBtn = res.status === 'RESERVED';
                                        let statusText = '';
                                        let statusClass = '';

                                        switch (res.status) {
                                            case 'RESERVED': statusText = '예약됨'; statusClass = 'pending'; break;
                                            case 'CANCELLED': statusText = '취소됨'; statusClass = 'cancelled'; break;
                                            default: statusText = res.status; statusClass = 'default';
                                        }

                                        return (
                                            <div key={res.reservationId} className="reservation-item">
                                                <span className="reservation-item__info">
                                                    {formatTime(res.departureTime)} 출발 ({res.plateNumber})
                                                </span>
                                                <div className="reservation-item__actions">
                                                    <span className={`status status--${statusClass}`}>
                                                        {statusText}
                                                    </span>
                                                    {showCancelBtn && (
                                                        <button
                                                            className="btn-cancel"
                                                            onClick={() => handleCancelReservation(res.reservationId, 'bus')}
                                                        >
                                                            취소
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })
                                )}
                            </div>
                        </section>

                        <section className="reservation-section">
                            <h3 className="reservation-section__title">
                                <RestaurantIcon className="icon icon-restaurant" /> 석식 신청
                            </h3>
                            <div className="reservation-list">
                                {reservations.dinnerReservations.length === 0 ? (
                                    <div className="reservation-empty">예약된 내역이 없습니다.</div>
                                ) : (
                                    reservations.dinnerReservations.map((res) => {
                                        const showCancelBtn = res.status === 'RESERVED';
                                        const statusText = res.status === 'RESERVED' ? '신청됨' : '취소됨';
                                        const statusClass = res.status === 'RESERVED' ? 'pending' : 'cancelled';

                                        return (
                                            <div key={res.reservationId} className="reservation-item">
                                                <span className="reservation-item__info">
                                                    {formatTime(res.dinnerTime)} 석식
                                                </span>
                                                <div className="reservation-item__actions">
                                                    <span className={`status status--${statusClass}`}>
                                                        {statusText}
                                                    </span>
                                                    {showCancelBtn && (
                                                        <button
                                                            className="btn-cancel"
                                                            onClick={() => handleCancelReservation(res.reservationId, 'dinner')}
                                                        >
                                                            취소
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })
                                )}
                            </div>
                        </section>
                    </>
                )}
            </div>
        </Modal>
    );
};
